import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";

const SUBJECTS = [
  "General Enquiry",
  "Custom Commission",
  "Interior Designer Trade",
  "Press & Editorial",
  "Care & Restoration",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("We'll be in touch shortly");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: SUBJECTS[0],
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Could not send just now. Please try WhatsApp."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-16">
        <div className="overline">Say hello</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl font-light text-[#1B3B36] leading-[1.02] tracking-tight max-w-4xl">
          A conversation, <em className="italic text-[#84968C]">not a form.</em>
        </h1>
        <p className="mt-8 text-[16px] leading-relaxed text-[#4A4A4A] font-light max-w-2xl">
          Whether it's a commission, a partnership, or just a question about
          knot density — we love hearing from considered homes.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <div className="border-t border-[#1B3B36] pt-6">
            <div className="overline">Studio</div>
            <div className="mt-3 font-serif text-2xl text-[#1B3B36] leading-tight flex items-start gap-3">
              <MapPin size={18} className="mt-2 text-[#84968C]" />
              17, Lavelle Lane
              <br /> Bengaluru 560 001
            </div>
          </div>
          <div className="border-t border-[#1B3B36] pt-6">
            <div className="overline">Direct</div>
            <div className="mt-3 space-y-3 font-serif text-2xl text-[#1B3B36]">
              <a
                href="mailto:hello@iryaahomes.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 link-underline"
              >
                <Mail size={18} className="text-[#84968C]" />
                hello@iryaahomes.com
              </a>
              <a
                href="tel:+918971188977"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 link-underline"
              >
                <Phone size={18} className="text-[#84968C]" />
                +91 89711 88977
              </a>
            </div>
          </div>
          <div className="border-t border-[#1B3B36] pt-6">
            <div className="overline">Fastest reply</div>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-link"
              className="mt-4 inline-flex items-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-6 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26]"
            >
              WhatsApp us <Send size={14} />
            </a>
            <p className="mt-4 text-[13px] text-[#4A4A4A] font-light max-w-sm">
              Monday – Saturday · 10.00 to 19.00 IST. Our team usually replies
              within an hour.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 border border-[#D8D4CC] p-8 md:p-10"
        >
          {sent ? (
            <div className="text-center py-16">
              <div className="overline text-[#84968C]">Thank you</div>
              <div className="mt-4 font-serif text-4xl text-[#1B3B36]">
                Your note has landed on our desk.
              </div>
              <p className="mt-4 text-[15px] text-[#4A4A4A] font-light">
                We'll write back within one working day.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-8 border border-[#1B3B36] text-[#1B3B36] px-6 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#1B3B36] hover:text-[#F9F8F6]"
              >
                Send another
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <span className="overline">Your name</span>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none"
                />
              </label>
              <label className="block">
                <span className="overline">Email</span>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none"
                />
              </label>
              <label className="block">
                <span className="overline">Phone (optional)</span>
                <input
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none"
                />
              </label>
              <label className="block">
                <span className="overline">Nature of enquiry</span>
                <select
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={(e) => onChange("subject", e.target.value)}
                  className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none"
                >
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="overline">Your message</span>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => onChange("message", e.target.value)}
                  rows={5}
                  className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none resize-none"
                />
              </label>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="md:col-span-2 mt-4 inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] transition-colors disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Sending…
                  </>
                ) : (
                  <>Send the message</>
                )}
              </button>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
